// Authentication API functions

import { apiClient } from './client'
import type { User, LoginRequest, LoginResponse } from './types'

/**
 * Fetch the current authenticated user
 * @returns User object if authenticated, null otherwise
 */
export async function fetchCurrentUser(): Promise<User | null> {
  try {
    const response = await apiClient.get<{ id: number; username: string; role: string }>('/api/v4/me')
    return {
      id: response.id,
      username: response.username,
      role: response.role as 'admin' | 'instructor' | 'trainee',
    }
  } catch {
    // 401 errors are handled by client (redirect to login)
    // Other errors mean user is not authenticated
    return null
  }
}

/**
 * Login with username and password
 * @param credentials Username and password
 * @returns Login response with user data or error
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  try {
    const response = await apiClient.post<LoginResponse>('/api/v4/auth/login', credentials)
    return response
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Login failed',
    }
  }
}

/**
 * Logout the current user
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post('/api/v4/auth/logout')
  } catch (error) {
    // Ignore logout errors - just clear client state
    console.error('Logout error:', error)
  }
}
